import React, { useCallback, useMemo } from 'react';
import { Select, MenuItem, SelectProps } from '@mui/material';
import { decode } from '../../Services/DecodeToken';
import {
  EmptyStateItem,
  SEARCHABLE_SELECT_WIDTH,
  SearchableSelectSearchBar,
  defaultOptionSx,
  selectFocusSx,
  selectedOptionSx,
  useSearchableSelect,
} from './SearchableSelectShared';

interface Company {
  CID: string | number;
  name: string;
}

interface CompanySelectProps extends Omit<SelectProps, 'value' | 'onChange'> {
  companies: Company[];
  selectedCompany: any;
  onChange: any;
  defaultName?: string;
  disabled?: boolean;
  searchable?: boolean;
}

export const CompanySelect: React.FC<CompanySelectProps> = ({
  companies,
  selectedCompany,
  onChange,
  defaultName = 'All Companies',
  disabled = false,
  searchable = true,
  ...props
}) => {
  const token: any = decode();
  const isAdmin = token.roles.id === 1;

  const handleSelect = useCallback((value: string) => {
    onChange(value === "" ? null : value);
  }, [onChange]);

  const sortedCompanies = useMemo(
    () =>
      [...companies].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ),
    [companies]
  );

  const {
    search,
    setSearch,
    open,
    handleOpen,
    handleClose,
    searchInputRef,
    selectRef,
    menuProps,
    handleSearchKeyDown: baseSearchKeyDown,
  } = useSearchableSelect({
    searchable,
    onEnterSelect: () => {
      const query = search.trim().toLowerCase();
      const matches = query
        ? sortedCompanies.filter((item) => item.name.toLowerCase().includes(query))
        : sortedCompanies;
      if (matches.length > 0) {
        handleSelect(matches[0].CID.toString());
        handleClose();
      }
    },
  });

  const filteredCompanies = useMemo(() => {
    if (!searchable || !search.trim()) return sortedCompanies;
    const query = search.trim().toLowerCase();
    return sortedCompanies.filter((item) => item.name.toLowerCase().includes(query));
  }, [sortedCompanies, search, searchable]);

  const resultLabel = search.trim()
    ? filteredCompanies.length === 0
      ? 'No matches'
      : `${filteredCompanies.length} match${filteredCompanies.length === 1 ? '' : 'es'}`
    : `${sortedCompanies.length} compan${sortedCompanies.length === 1 ? 'y' : 'ies'}`;

  const onSelectChange = (value: string) => {
    handleSelect(value);
    handleClose();
  };

  return (
    <Select
      {...props}
      ref={selectRef}
      disabled={disabled}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      size="small"
      value={selectedCompany || ""}
      onChange={(e) => onSelectChange(e.target.value as string)}
      displayEmpty
      renderValue={() => {
        if ((!selectedCompany || selectedCompany === "") && isAdmin) {
          return defaultName;
        }

        const selected = companies.find(
          (item) => item.CID.toString() === selectedCompany?.toString()
        );
        return selected?.name ?? 'Select Company';
      }}
      MenuProps={menuProps}
      sx={{
        minWidth: { xs: 0, sm: SEARCHABLE_SELECT_WIDTH },
        width: { xs: '100%', sm: 'auto' },
        maxWidth: '100%',
        ...selectFocusSx,
        ...props.sx,
      }}
    >
      {searchable && (
        <SearchableSelectSearchBar
          placeholder="Search companies..."
          search={search}
          onSearchChange={setSearch}
          onKeyDown={baseSearchKeyDown}
          resultLabel={resultLabel}
          searchInputRef={searchInputRef}
        />
      )}

      {isAdmin && (
        <MenuItem value="" sx={defaultOptionSx}>
          <em>{defaultName}</em>
        </MenuItem>
      )}

      {filteredCompanies.length > 0 ? (
        filteredCompanies.map((item) => (
          <MenuItem
            key={item.CID}
            value={item.CID.toString()}
            selected={item.CID.toString() === selectedCompany?.toString()}
            sx={selectedOptionSx}
          >
            {item.name}
          </MenuItem>
        ))
      ) : (
        searchable &&
        search.trim() && (
          <EmptyStateItem disabled value="__no_results__">
            No companies found
          </EmptyStateItem>
        )
      )}
    </Select>
  );
};
